
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";

import { Head } from "@/components/Head";
import { Heading } from "@/components/Heading";
import { Image } from "@/components/Image";
import { Grid } from "@/components/Grid";
import { BlogText } from "@/components/BlogText";
import { CTASection } from "@/sections/CTASection";
import { useTheme } from "@/hooks/useTheme";

const OpenXdFileFlinto = () => {
  const { colors } = useTheme();
  return (
    <>
      <Head
        pageNamespace="page/open-xd-file-flinto"
        ogImage="/images/main-og-image.jpg"
      />

      <div>
        <Grid
          customStyle={{
            maxWidth: 800,
            flexDirection: "column",
            alignItems: "flex-start",
            paddingBottom: "0 !important",
          }}
        >
          <Heading as="h1">Open XD files in Flinto</Heading>
          <BlogText>
            Currently, it is not possible to import Adobe XD files into Flinto,
            but we have a simple and effective way to enjoy the prototyping
            capabilities of Flinto by using our converter.
          </BlogText>
          <BlogText>
            Firstly we need to convert the XD file into a format that Flinto
            will accept.
          </BlogText>
          <BlogText>
            We can do this using our XD to Sketch/Figma converter and then
            import the files into Flinto directly.
          </BlogText>
          <BlogText>
            <strong>Please note:</strong>
            &nbsp; You will need a working version of Sketch OR a Figma account,
            as Flinto requires these to import the files. You should convert
            into the appropriate format.
          </BlogText>
          <BlogText>Let’s get started.</BlogText>

          <section>
            <Heading as="h3">Convert XD file to Sketch or Figma</Heading>
            <ol>
              <li>
                <BlogText>
                  <strong>Upload your file to Magicul</strong>
                  &nbsp;-&nbsp;go to our &nbsp;
                  <a
                    href="/"
                    style={{
                      color: colors.RED,
                      fontWeight: "bold",
                    }}
                  >
                    XD to Sketch converter
                  </a>
                  or
                  <a
                    href="/converter/xd-to-figma"
                    style={{
                      color: colors.RED,
                      fontWeight: "bold",
                    }}
                  >
                    XD to Figma converter
                  </a>
                  . Then drag a file to the converter or click on browse to
                  upload a file.
                </BlogText>
                <Image
                  customStyle={{ width: "100%", marginBottom: 29 }}
                  src="/images/tutorials/uploading-image.jpg"
                  alt="Magicul uploading dialog"
                />
              </li>
              <li>
                <BlogText>
                  <strong>Select your plan</strong>
                  &nbsp;- Click on convert now and select your plan.
                </BlogText>
              </li>
              <li>
                <BlogText>
                  <strong>Complete the checkout</strong>
                  &nbsp;-&nbsp;enter your details and complete the checkout.
                  Your file will now convert from the XD to Sketch/Figma.
                </BlogText>
              </li>
              <li>
                <BlogText>
                  <strong>Download your file</strong>
                  &nbsp;-&nbsp;click the arrow button to download the converted
                  file.
                </BlogText>
                <Image
                  customStyle={{ width: "100%", marginBottom: 29 }}
                  src="/images/tutorials/downloading-image.jpg"
                  alt="xd2sketch downloading dialog"
                />
              </li>
            </ol>
            <BlogText>
              Now we have a Sketch or Figma file that can be imported into
              UXPin.
            </BlogText>
          </section>
          <section>
            <Heading as="h2">Import your file into Flinto</Heading>
            <section>
              <Heading as="h3">Sketch method</Heading>
              <ol>
                <li>
                  <BlogText>Open the converted file in Sketch.</BlogText>
                </li>
                <li>
                  <BlogText>Open Flinto.</BlogText>
                </li>
                <li>
                  <BlogText>Click the Import button.</BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-in-flinto/flinto-import.jpg"
                    alt="flinto import"
                  />
                </li>
                <li>
                  <BlogText>
                    Flinto will detect the open Sketch file and the active page
                    that you are on in Sketch.
                  </BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-in-flinto/flinto-import-sketch.jpg"
                    alt="flinto import sketch"
                  />
                </li>
              </ol>
              <BlogText>
                You can now use Flinto with your converted XD File.
              </BlogText>
              <CTASection
                title="Want to convert XD files to Flinto?"
                buttonLabel="Start Now"
                href="/"
              />
            </section>

            <section>
              <Heading as="h3">Figma method</Heading>
              <ol>
                <li>
                  <BlogText>Upload the converted file to Figma.</BlogText>
                </li>
                <li>
                  <BlogText>Open Flinto.</BlogText>
                </li>
                <li>
                  <BlogText>Click the Import button.</BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-in-flinto/flinto-import.jpg"
                    alt="flinto import"
                  />
                </li>
                <li>
                  <BlogText>Click Authorize Figma and login to Figma.</BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-in-flinto/flinto-authorization-dialog.jpg"
                    alt="flinto authorization dialog"
                  />
                </li>
                <li>
                  <BlogText>
                    Click&nbsp;
                    <strong>Allow Access</strong>
                    &nbsp;to give Flinto access to Figma.
                  </BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-in-flinto/flinto-permission.jpg"
                    alt="flinto permission"
                  />
                </li>
                <li>
                  <BlogText>Now select the file from Figma in Flinto</BlogText>
                  <Image
                    customStyle={{ width: "100%", marginBottom: 29 }}
                    src="/images/tutorials/open-xd-in-flinto/flinto-figma-select.jpg"
                    alt="flinto figma select dialog"
                  />
                </li>
              </ol>
              <BlogText>
                You can now use your converted XD file in Flinto, happy
                prototyping!
              </BlogText>
              <CTASection
                title="Want to convert XD files to Flinto?"
                buttonLabel="Start Now"
                href="/"
              />
            </section>
          </section>
        </Grid>
      </div>
    </>
  );
};

export default OpenXdFileFlinto;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        
// @ts-ignore
        
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          
// @ts-ignore
          props: {
// @ts-ignore
            
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/tutorials/open-xd-file-flinto',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  